import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { GoogleMap, MapGeocoder, MapMarker } from '@angular/google-maps';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-location-picker',
  templateUrl: './location-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslatePipe,
    GoogleMap,
    MapMarker,
  ],
})
export class LocationPickerComponent implements OnInit {
  private readonly geocoder = inject(MapGeocoder);

  readonly latControl = input.required<FormControl<number>>();
  readonly lngControl = input.required<FormControl<number>>();
  readonly hintLat = input(0);
  readonly hintLng = input(0);

  private static readonly DEFAULT_CENTER = { lat: 52.092876, lng: 5.10448 };
  private static readonly DEFAULT_ZOOM = 5;
  private static readonly DETAIL_ZOOM = 14;

  readonly mapsReady = signal(!!(window as { google?: { maps?: unknown } }).google?.maps);
  readonly mapCenter = signal<{ lat: number; lng: number }>(LocationPickerComponent.DEFAULT_CENTER);
  readonly mapZoom = signal(LocationPickerComponent.DEFAULT_ZOOM);
  readonly markerPosition = signal<{ lat: number; lng: number } | null>(null);
  readonly addressQuery = signal('');

  constructor() {
    effect(() => {
      const hLat = this.hintLat();
      const hLng = this.hintLng();
      if ((hLat !== 0 || hLng !== 0) && this.markerPosition() === null) {
        this.mapCenter.set({ lat: hLat, lng: hLng });
        this.mapZoom.set(LocationPickerComponent.DETAIL_ZOOM);
      }
    });
  }

  ngOnInit(): void {
    const lat = this.latControl().value;
    const lng = this.lngControl().value;
    if (lat !== 0 || lng !== 0) {
      this.setPosition(lat, lng);
    }
  }

  private setPosition(lat: number, lng: number): void {
    const pos = { lat, lng };
    this.markerPosition.set(pos);
    this.mapCenter.set(pos);
    this.mapZoom.set(LocationPickerComponent.DETAIL_ZOOM);
  }

  /**
   * Push a picked position onto the bound controls. `setValue` leaves a control pristine, so the explicit
   * `markAsDirty` is what lets a parent that submits only what changed — the water form skips its PUT
   * unless `form.dirty` — notice that the pin was moved.
   */
  private writeCoordinates(lat: number, lng: number): void {
    this.latControl().setValue(lat);
    this.lngControl().setValue(lng);
    this.latControl().markAsDirty();
    this.lngControl().markAsDirty();
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();
    if (lat == null || lng == null) return;
    this.setPosition(lat, lng);
    this.writeCoordinates(lat, lng);
  }

  onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();
    if (lat == null || lng == null) return;
    this.markerPosition.set({ lat, lng });
    this.writeCoordinates(lat, lng);
  }

  searchAddress(): void {
    const query = this.addressQuery().trim();
    if (!query) return;
    this.geocoder.geocode({ address: query }).subscribe(({ results, status }) => {
      if (status === 'OK' && results.length > 0) {
        const loc = results[0].geometry.location;
        this.setPosition(loc.lat(), loc.lng());
        this.writeCoordinates(loc.lat(), loc.lng());
      }
    });
  }
}
